/**
 * Digests & Email Tab
 *
 * Groups settings by digest type: Morning, Evening, Weekly.
 * Each section contains its toggle/schedule, variety slider (morning only),
 * custom AI instructions, and send-test button.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import {
  Mail,
  Sun,
  Moon,
  CalendarDays,
  Check,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { btn } from '../buttonStyles';
import {
  type DigestVarietyMode,
  getUserSettings,
  updateDigestSettings,
  updateDigestVarietyMode,
  updateWeeklyDigestSettings,
  getCaptureEmail,
  rotateCaptureEmail,
  initCaptureEmail,
} from '../../../api/userSettings';
import { DigestInstructionSection } from '../../DigestSettings';

const DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export function DigestsEmailTab() {
  // Morning digest state
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestTime, setDigestTime] = useState('07:00');
  const [varietyMode, setVarietyMode] = useState<DigestVarietyMode>('deterministic');

  // Evening digest state
  const [eveningEnabled, setEveningEnabled] = useState(false);
  const [eveningTime, setEveningTime] = useState('18:00');

  // Weekly digest state
  const [weeklyEnabled, setWeeklyEnabled] = useState(false);
  const [weeklyDay, setWeeklyDay] = useState('sunday');
  const [weeklyTime, setWeeklyTime] = useState('18:00');

  // Track initial load to skip auto-save on mount
  const loaded = useRef(false);

  // Email capture state
  const [captureEmail, setCaptureEmail] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [copiedCapture, setCopiedCapture] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getUserSettings().then((s) => {
        setDigestEnabled(s.dailyDigestEnabled);
        setDigestTime(s.digestTime);
        setEveningEnabled(s.eveningDigestEnabled ?? false);
        setEveningTime(s.eveningDigestTime ?? '18:00');
        setWeeklyEnabled(s.weeklyDigestEnabled);
        setWeeklyDay(s.weeklyDigestDay ?? 'sunday');
        setWeeklyTime(s.weeklyDigestTime ?? '18:00');
        setVarietyMode(s.digestVarietyMode ?? 'deterministic');
      }),
      getCaptureEmail()
        .then((r) => setCaptureEmail(r.address))
        .catch(async () => {
          try {
            const r = await initCaptureEmail();
            setCaptureEmail(r.address);
          } catch {
            // address shows as '...' until next load
          }
        }),
    ])
      .catch(() => setError('Failed to load settings'))
      .finally(() => { setIsLoading(false); loaded.current = true; });
  }, []);

  // Auto-save morning settings on change
  const autoSave = useCallback(async (fn: () => Promise<unknown>, label: string) => {
    try { await fn(); } catch { setError(`Failed to save ${label}`); }
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    void autoSave(() => updateDigestSettings({ dailyDigestEnabled: digestEnabled, digestTime, digestTimezone: tz }), 'morning digest');
  }, [digestEnabled, digestTime, autoSave]);

  useEffect(() => {
    if (!loaded.current) return;
    void autoSave(() => updateDigestSettings({ eveningDigestEnabled: eveningEnabled, eveningDigestTime: eveningTime } as any), 'evening digest');
  }, [eveningEnabled, eveningTime, autoSave]);

  useEffect(() => {
    if (!loaded.current) return;
    void autoSave(() => updateWeeklyDigestSettings({ weeklyDigestEnabled: weeklyEnabled, weeklyDigestDay: weeklyDay, weeklyDigestTime: weeklyTime }), 'weekly digest');
  }, [weeklyEnabled, weeklyDay, weeklyTime, autoSave]);

  const handleVarietyChange = async (mode: DigestVarietyMode) => {
    setVarietyMode(mode);
    void autoSave(() => updateDigestVarietyMode(mode), 'variety mode');
  };

  const handleCopyCaptureEmail = async () => {
    if (!captureEmail) return;
    await navigator.clipboard.writeText(captureEmail);
    setCopiedCapture(true);
    setTimeout(() => setCopiedCapture(false), 2000);
  };

  const handleRotateCaptureEmail = async () => {
    setIsRotating(true);
    setShowRotateConfirm(false);
    try {
      const result = await rotateCaptureEmail();
      setCaptureEmail(result.address);
    } catch {
      setError('Failed to rotate email address');
    } finally {
      setIsRotating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  const varietyModes: DigestVarietyMode[] = ['deterministic', 'smart', 'lessSmart', 'chaos'];
  const varietyLabels = ['Deterministic', 'Smart Variety', 'Less Smart', 'Call Me Chaos'];
  const varietySubtexts = [
    'Same smart picks every time. Your most critical tasks, no surprises.',
    'Your urgent tasks always show up. The rest rotate from your backlog.',
    'One anchor task, four wildcards drawn across your backlog.',
    'Pure randomness. You\'ll never see the same digest twice.',
  ];
  const varietyIdx = varietyModes.indexOf(varietyMode);

  return (
    <>
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* ── Morning Digest ── */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sun className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Morning Digest
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          A morning email with your top 3-5 tasks for the day, curated by Danny.
        </p>

        <div className="space-y-4">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                Enable morning digest
              </p>
            </div>
            <button
              role="switch"
              aria-checked={digestEnabled}
              onClick={() => setDigestEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                digestEnabled
                  ? 'bg-primary-500'
                  : 'bg-border-hover-light dark:bg-border-hover-dark'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  digestEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Time picker */}
          {digestEnabled && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                Delivery time
              </p>
              <input
                type="time"
                value={digestTime}
                onChange={(e) => setDigestTime(e.target.value)}
                className="px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          {/* Variety mode */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
              Task variety
            </p>
            {/* Pill track with sliding thumb */}
            <div
              className="relative h-8 rounded-full border border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-surface-base-dark cursor-pointer"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                const stop = Math.round(pct * (varietyModes.length - 1));
                handleVarietyChange(varietyModes[Math.max(0, Math.min(stop, varietyModes.length - 1))]);
              }}
            >
              {/* Stop dots */}
              {varietyModes.map((_, i) => {
                const pad = 14;
                return (
                  <div
                    key={i}
                    className={clsx(
                      'absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full',
                      i === varietyIdx ? 'opacity-0' : 'bg-border-hover-light dark:bg-border-hover-dark',
                    )}
                    style={{ left: `calc(${(i / (varietyModes.length - 1)) * 100}% * (1 - ${2 * pad}px / 100%) + ${pad}px - 4px)` }}
                  />
                );
              })}
              {/* Thumb */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(8,252,221,0.4)] transition-all duration-200"
                style={{ left: `calc(${(varietyIdx / (varietyModes.length - 1)) * 100}% * (1 - 28px / 100%) + 2px)` }}
              />
            </div>
            {/* Labels — centered over stops */}
            <div className="relative h-4" style={{ padding: '0 14px' }}>
              {varietyLabels.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleVarietyChange(varietyModes[i])}
                  className={clsx(
                    'absolute font-mono text-[10px] tracking-wide cursor-pointer transition-colors -translate-x-1/2',
                    i === 0 && 'translate-x-0',
                    i === varietyModes.length - 1 && '-translate-x-full',
                    i === varietyIdx
                      ? 'text-primary-500 font-bold'
                      : 'text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark',
                  )}
                  style={{ left: `${(i / (varietyModes.length - 1)) * 100}%` }}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              {varietySubtexts[varietyIdx]}
            </p>
          </div>

        </div>

        {/* Morning instructions */}
        <DigestInstructionSection digestType="morning" />
      </div>

      {/* ── Evening Digest ── */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Evening Digest
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Sent at the end of your day. Reflects on progress and sets up tomorrow.
        </p>

        <div className="space-y-4">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
              Enable evening digest
            </p>
            <button
              role="switch"
              aria-checked={eveningEnabled}
              onClick={() => setEveningEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                eveningEnabled
                  ? 'bg-primary-500'
                  : 'bg-border-hover-light dark:bg-border-hover-dark'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  eveningEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Time picker */}
          {eveningEnabled && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                Delivery time
              </p>
              <input
                type="time"
                value={eveningTime}
                onChange={(e) => setEveningTime(e.target.value)}
                className="px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

        </div>

        {/* Evening instructions */}
        <DigestInstructionSection digestType="evening" />
      </div>

      {/* ── Weekly Digest ── */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Weekly Digest
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          A weekly AI-generated recap of what you accomplished.
        </p>

        <div className="space-y-4">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
              Enable weekly summary
            </p>
            <button
              role="switch"
              aria-checked={weeklyEnabled}
              onClick={() => setWeeklyEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                weeklyEnabled
                  ? 'bg-primary-500'
                  : 'bg-border-hover-light dark:bg-border-hover-dark'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  weeklyEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Day and time pickers */}
          {weeklyEnabled && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                  Delivery day
                </p>
                <select
                  value={weeklyDay}
                  onChange={(e) => setWeeklyDay(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {DAYS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                  Delivery time
                </p>
                <input
                  type="time"
                  value={weeklyTime}
                  onChange={(e) => setWeeklyTime(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </>
          )}

        </div>

        {/* Weekly instructions */}
        <DigestInstructionSection digestType="weekly" />
      </div>

      {/* ── Email Capture ── */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Email Capture
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Send tasks to Danny from anywhere by emailing your unique address. Add{' '}
          <code className="text-xs bg-surface-base-light dark:bg-surface-base-dark px-1 py-0.5 rounded">
            +tag
          </code>{' '}
          to label tasks automatically.
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark font-mono truncate">
              {captureEmail ?? '...'}
            </code>
            <button
              onClick={handleCopyCaptureEmail}
              disabled={!captureEmail}
              className={`${btn.secondary.sm} flex items-center gap-1.5`}
            >
              {copiedCapture ? <Check className="w-3.5 h-3.5 text-green-500" /> : null}
              {copiedCapture ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={() => setShowRotateConfirm(true)}
              disabled={isRotating}
              className={`${btn.secondary.sm} flex items-center gap-1.5`}
            >
              {isRotating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {isRotating ? 'Rotating...' : 'Rotate'}
            </button>
          </div>

          {showRotateConfirm && (
            <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
              <span className="text-amber-800 dark:text-amber-200">
                This will invalidate your old address. Continue?
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowRotateConfirm(false)}
                  className={btn.ghost.xs}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRotateCaptureEmail}
                  className={btn.primary.xs}
                >
                  Yes, rotate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
